import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { money } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const card = {
  hidden: { opacity: 0, y: 50 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: i * 0.1 } }),
};

export const Collections = React.forwardRef((props, ref) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${API}/shop/products`)
      .then((r) => setProducts(r.data))
      .catch(() => toast.error("Could not load the arsenal"))
      .finally(() => setLoading(false));
  }, []);

  const slots = Math.max(4, products.length);
  const placeholders = Math.max(0, slots - products.length);

  return (
    <section id="collections" ref={ref} data-testid="collections" className="relative bg-[#050505] py-24 md:py-32">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-end justify-between mb-16 flex-wrap gap-6"
        >
          <div>
            <span className="font-sans text-[11px] tracking-[0.45em] text-white/40">THE ARSENAL</span>
            <h2 className="mt-5 font-display font-black uppercase tracking-normal leading-none text-white text-5xl md:text-7xl">
              Choose your armor.
            </h2>
          </div>
          <p className="font-sans text-sm text-white/50 max-w-xs">
            Every piece cut, weighted and finished in matte black. Made on demand, shipped worldwide.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {loading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div key={`s${i}`} className="bg-[#0d0d0d] border border-white/10 min-h-[420px] animate-pulse" />
            ))}

          {!loading &&
            products.map((p, i) => (
              <motion.article
                key={p.id}
                data-testid={`collection-card-${p.handle}`}
                custom={i}
                variants={card}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                whileHover={{ y: -8 }}
                onClick={() => navigate(`/product/${p.handle}`)}
                className="group relative bg-[#0d0d0d] border border-white/10 hover:border-white/25 transition-colors duration-500 flex flex-col cursor-pointer"
              >
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none spotlight" />
                <div className="relative flex items-start justify-between p-7 pb-0">
                  <div>
                    <h3 className="font-display font-extrabold uppercase tracking-normal text-xl text-white leading-tight max-w-[12ch]">{p.title}</h3>
                    <p className="font-sans text-xs tracking-[0.15em] text-white/45 mt-1 uppercase">{p.tags?.[0] || "Lone Wolf"}</p>
                  </div>
                  {!p.available && (
                    <span className="font-sans text-[9px] tracking-[0.2em] text-white/70 border border-white/30 px-2 py-1">SOLD OUT</span>
                  )}
                </div>
                <div className="relative flex-1 flex items-center justify-center px-4 py-6 min-h-[240px]">
                  {p.image ? (
                    <img src={p.image} alt={p.title} className="max-h-[240px] w-full object-contain transition-transform duration-700 group-hover:scale-105" />
                  ) : (
                    <div className="w-full h-[220px] bg-[#141414]" />
                  )}
                </div>
                <div className="relative p-7 pt-0 flex items-center justify-between border-t border-white/[0.06] mt-auto pt-5">
                  <span className="font-sans text-sm text-white/70">{money(p.minPrice, p.currency)}</span>
                  <button
                    data-testid={`view-${p.handle}`}
                    onClick={(e) => { e.stopPropagation(); navigate(`/product/${p.handle}`); }}
                    className="flex items-center gap-2 font-sans text-[11px] tracking-[0.2em] text-white/70 hover:text-white transition-colors"
                  >
                    VIEW
                    <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </div>
              </motion.article>
            ))}

          {!loading &&
            Array.from({ length: placeholders }).map((_, i) => (
              <motion.div
                key={`ph${i}`}
                custom={products.length + i}
                variants={card}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                className="relative bg-[#0a0a0a] border border-dashed border-white/10 flex flex-col items-center justify-center min-h-[420px] text-center px-6"
              >
                <span className="font-display font-black uppercase tracking-normal text-2xl text-white/25">Coming Soon</span>
                <p className="font-sans text-[11px] tracking-[0.2em] text-white/25 mt-3">NEXT DROP LOADING</p>
              </motion.div>
            ))}
        </div>
      </div>
    </section>
  );
});

Collections.displayName = "Collections";
export default Collections;
